import { useEffect, useState } from 'react';

// Shared across components, so a size measured in one place (a suggestion's
// thumbnail) is known at once in another (the cover it is picked into).
const sizes = new Map();
// Past this, the oldest measurement goes — a Map iterates in insertion order.
const MAX_SIZES = 300;

/** The pixel size of the image at `url` as `{ width, height }` — null until it loads, and for one that fails. */
export default function useImageSize(url) {
  // Kept alongside the shared map, so an eviction doesn't blank a size still on screen.
  const [measured, setMeasured] = useState(null);

  useEffect(() => {
    if (!url) return undefined;
    if (sizes.has(url)) {
      setMeasured({ url, size: sizes.get(url) });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      sizes.set(url, size);
      if (sizes.size > MAX_SIZES) sizes.delete(sizes.keys().next().value);
      if (!cancelled) setMeasured({ url, size });
    };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);

  if (!url) return null;
  return sizes.get(url) || (measured && measured.url === url ? measured.size : null);
}
